import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  FormGroup,
  FormControlLabel,
  Grid,
  Grow,
  Icon,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  tooltipClasses,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles, styled } from "@mui/styles";
import { useTranslation } from "react-i18next";

import Search from "@mui/icons-material/Search";
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg";
import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg";
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg";
import { ReactComponent as FilterStatusIcon } from "../../../../assets/icons/filter-status.svg";

import CustomPagination from "../../../../components/CustomPagination";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import HistoryDialog from "../../../../components/corporateGoal/HistoryDialog";

import {
  getGoalDashboard,
  postGoalFormStatusEmployeeDashboard,
  postGoalProgressEmployeeDashboard,
  postGoalProgressLogDashboard,
} from "../../../../actions/managerDashboard";

import ApexCharts from "react-apexcharts";
import * as Util from "../../../../services/global.service";
import cssVariables from "../../../../theme/cssVariables";
import moment from "moment";
import debounce from "lodash.debounce";

const columnWidths = {
  formStatus: [
    "calc(35% - 12px)",
    "calc(35% - 12px)",
    "calc(12% - 12px)",
    "calc(12% - 12px)",
    "calc(10% - 12px)",
  ],
  progress: [
    "calc(25% - 12px)",
    "calc(25% - 12px)",
    "calc(13% - 12px)",
    "calc(13% - 12px)",
    "calc(13% - 12px)",
    "calc(13% - 12px)",
    "calc(13% - 12px)",
  ],
};
const goalFormStatusBaseFilters = {
  cycle_id: "",
  organization_id: "",
  form_status: "draft,pending,approved",
  limit: 50,
  page: 1,
};
const goalProgressBaseFilters = {
  cycle_id: "",
  organization_id: "",
  limit: 50,
  page: 1,
};

const GoalManagerDashboard = (props) => {
  const { t, i18n } = useTranslation();
  const managerDashboardState = useSelector((state) => state.ManagerDashboard);
  const dispatch = useDispatch();
  const useStyles = makeStyles({
    flexPaper: {
      flex: 1,
      minWidth: 350,
      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)!important",
      borderRadius: "10px!important",
    },
    paperRoot: {
      display: "flex",
      width: "100%",
      flexWrap: "wrap",
    },
    toggleBtnRoot: {
      backgroundColor: "#FFF",
    },
    toggleBtnSelected: {
      backgroundColor: `${cssVariables.color.primary}!important`,
      border: "1px solid #DADADA!important",
      color: "#FFF!important",
    },
    checkGreenSvg: {
      background: "#FFF",
      borderRadius: "50%",
    },
  });
  const classes = useStyles();

  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: "#343A40",
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#343A40",
      color: "#FFFFFF",
      padding: "11px 15px",
    },
  }));

  let formGoalChartOptions = {
    series: [],
    options: {
      chart: {
        offsetY: 10,
      },
      pie: {
        donut: {
          labels: {
            show: false,
            name: {
              show: true,
              offsetY: 25,
              formatter: () => t("mygoal.label.complete"),
            },
            value: {
              show: true,
              fontSize: "30px",
              fontFamily: "Kanit",
              fontWeight: 600,
              color: undefined,
              offsetY: -15,
              color: "#5DA196",
              formatter: function (val) {
                return val;
              },
            },
            total: {
              show: true,
              showAlways: true,
              fontSize: "14px",
              fontFamily: "Kanit",
              fontWeight: 300,
              color: "#A3A3A3",
              background: "#fff",
            },
          },
        },
      },
      colors: ["#5DA196", "#EFB76D", "#EAEAEA"],
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 1,
          },
        },
      },
      stroke: {
        width: 0,
        colors: ["#fff"],
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        enabledOnSeries: undefined,
        shared: true,
        followCursor: false,
        intersect: false,
        inverseOrder: false,
        custom: undefined,
        fillSeriesColor: false,
        theme: false,
        onDatasetHover: {
          highlightDataSeries: false,
        },
        style: {
          fontSize: "16px",
          fontFamily: "Kanit",
          fontWeight: 300,
        },
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: (seriesName) => seriesName + ":",
          },
          style: {
            fontSize: "16px",
            fontFamily: "Kanit",
            fontWeight: 300,
          },
          formatter: (val, opts) => {
            return `${val} (${parseFloat(
              opts.globals.seriesPercent[opts.seriesIndex]
            )?.toFixed(2)}%)`;
          },
        },
        marker: {
          show: true,
        },
        items: {
          display: "flex",
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        show: false,
      },
    },
  };
  let progressChartOptions = {
    series: [],
    options: {
      chart: {
        offsetY: 10,
      },
      pie: {
        donut: {
          labels: {
            show: false,
            name: {
              show: true,
              offsetY: 25,
              formatter: () => t("mygoal.label.complete"),
            },
            value: {
              show: true,
              fontSize: "30px",
              fontFamily: "Kanit",
              fontWeight: 600,
              color: undefined,
              offsetY: -15,
              color: "#5DA196",
              formatter: function (val) {
                return val;
              },
            },
            total: {
              show: true,
              showAlways: true,
              fontSize: "14px",
              fontFamily: "Kanit",
              fontWeight: 300,
              color: "#A3A3A3",
              background: "#fff",
            },
          },
        },
      },
      colors: ["#5DA196", "#EFB76D", "#EAEAEA"],
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 1,
          },
        },
      },
      stroke: {
        width: 0,
        colors: ["#fff"],
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        enabledOnSeries: undefined,
        shared: true,
        followCursor: false,
        intersect: false,
        inverseOrder: false,
        custom: undefined,
        fillSeriesColor: false,
        theme: false,
        onDatasetHover: {
          highlightDataSeries: false,
        },
        style: {
          fontSize: "16px",
          fontFamily: "Kanit",
          fontWeight: 300,
        },
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: (seriesName) => seriesName + ":",
          },
          style: {
            fontSize: "16px",
            fontFamily: "Kanit",
            fontWeight: 300,
          },
          formatter: (val, opts) => {
            return `${val} (${parseFloat(
              opts.globals.seriesPercent[opts.seriesIndex]
            )?.toFixed(2)}%)`;
          },
        },
        marker: {
          show: true,
        },
        items: {
          display: "flex",
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      fill: {
        opacity: 1,
        // type: ['solid', 'gradient'],
        // colors: '#F0F0F0'
      },
      legend: {
        show: false,
      },
    },
  };
  const [filtersQuery, setFiltersQuery] = React.useState({
    query: "",
    reqData: {
      organization_id: [],
      cycle_id: "",
    },
  });
  const [goalFormStatusFilters, setGoalFormStatusFilters] = React.useState(
    goalFormStatusBaseFilters
  );
  const [goalProgressFilters, setGoalProgressFilters] = React.useState(
    goalProgressBaseFilters
  );
  const [formGoalChart, setFormGoalChartData] =
    React.useState(formGoalChartOptions);
  const [formGoalRaw, setFormGoalRawData] = React.useState(0);
  const [progressChart, setProgressChartData] =
    React.useState(progressChartOptions);
  const [progressRaw, setProgressRawData] = React.useState(0);
  const [totalStatusForms, setTotalStatusForms] = React.useState(0);
  const [totalProgress, setTotalProgress] = React.useState(0);
  const [percentageFormStatus, setPercentageFormStatus] = React.useState([]);
  const [percentageProgress, setPercentageProgress] = React.useState([]);
  const [achievedData, setAchievedData] = React.useState([]);
  const [barPercentage, setBarPercentage] = React.useState([]);
  const [categoryData, setCategoryData] = React.useState(0);
  const [currentCycle, setCurrentCycle] = React.useState({});
  const [perPage, setPerPage] = React.useState(50);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPage, setTotalPage] = React.useState(1);
  const [currentSort, setCurrentSort] = React.useState({
    key: "",
    direction: "",
  });
  const [search, setSearch] = React.useState("");
  const [tableType, setTableType] = React.useState("form_status");
  const [list, setList] = React.useState([]);
  const [filteredList, setFilteredList] = React.useState([]);

  const anchorRefStatus = React.useRef(null);
  const [openStatus, setOpenStatus] = React.useState(false);
  const [checkboxStatus, setCheckboxStatus] = React.useState([
    "draft",
    "pending",
    "approved",
  ]);
  const [checkboxStatusList, setCheckboxStatusList] = React.useState([
    "draft",
    "pending",
    "approved",
  ]);
  const anchorRefProgress = React.useRef(null);
  const [openProgress, setOpenProgress] = React.useState(false);
  const [checkboxProgress, setCheckboxProgress] = React.useState([]);
  const [checkboxProgressList, setCheckboxProgressList] = React.useState([]);

  const anchorRefActualValue = React.useRef(null);
  const [openActualValue, setOpenActualValue] = React.useState(false);
  const [checkboxActualValue, setCheckboxActualValue] = React.useState([
    "Achieved",
    "Not Achieved",
  ]);
  const [checkboxActualValueList, setCheckboxActualValueList] = React.useState([
    "Achieved",
    "Not Achieved",
  ]);

  const anchorRefCategory = React.useRef(null);
  const [openCategory, setOpenCategory] = React.useState(false);
  const [checkboxCategory, setCheckboxCategory] = React.useState([]);
  const [checkboxCategoryList, setCheckboxCategoryList] = React.useState([]);

  const [openHistoryDialog, setHistoryDialog] = React.useState(false);
  const [historyData, setHistoryData] = React.useState([]);
  const [historyOptionalData, setHistoryOptionalData] = React.useState([]);
  const handleHistoryDialog = (val) => {
    setHistoryDialog(val);
  };

  const [goalProgressHeaders, setGoalProgressHeaders] = React.useState([
    { column: "employee" },
    { column: "goal_name" },
    { column: "category" },
    { column: "goal_start_at" },
    { column: "goal_end_at" },
    { column: "actual_value" },
  ]);
  const [firstLoaded, setFirstLoaded] = React.useState({
    category: false,
    progress: false
  })

  // checkbox status
  const handleStatusKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenStatus(false);
    } else if (event.key === "Escape") {
      setOpenStatus(false);
    }
  };
  const handleCloseStatus = (event) => {
    if (
      anchorRefStatus.current &&
      anchorRefStatus.current.contains(event.target)
    ) {
      return;
    }
    setOpenStatus(false);
  };
  const handleClickStatus = () => {
    setOpenStatus((prevOpen) => !prevOpen);
  };
  const handleCheckboxStatus = (val) => {
    let tmpCheckbox = [];
    if (val === "all") {
      if (
        checkboxStatus.length >= 0 &&
        checkboxStatus.length < checkboxStatusList.length
      ) {
        checkboxStatusList.forEach((status) => {
          tmpCheckbox.push(status);
        });
      } else {
        tmpCheckbox = [];
      }
    } else {
      if (checkboxStatus.includes(val)) {
        tmpCheckbox = [...checkboxStatus].filter((status) => status !== val);
      } else {
        tmpCheckbox = [...checkboxStatus, val];
      }
    }
    setCheckboxStatus(tmpCheckbox);
  };

  // checkbox progress
  const handleProgressKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenProgress(false);
    } else if (event.key === "Escape") {
      setOpenProgress(false);
    }
  };
  const handleCloseProgress = (event) => {
    if (
      anchorRefProgress.current &&
      anchorRefProgress.current.contains(event.target)
    ) {
      return;
    }
    setOpenProgress(false);
  };
  const handleClickProgress = () => {
    setOpenProgress((prevOpen) => !prevOpen);
  };
  const handleCheckboxProgress = (val) => {
    let tmpCheckbox = [];
    if (val === "all") {
      if (
        checkboxProgress.length >= 0 &&
        checkboxProgress.length < checkboxProgressList.length
      ) {
        checkboxProgressList.forEach((progress) => {
          tmpCheckbox.push(progress.id);
        });
      } else {
        tmpCheckbox = [];
      }
    } else {
      if (checkboxProgress.includes(val)) {
        tmpCheckbox = [...checkboxProgress].filter(
          (progress) => progress !== val
        );
      } else {
        tmpCheckbox = [...checkboxProgress, val];
      }
    }
    setCheckboxProgress(tmpCheckbox);
  };

  // checkbox actual value
  const handleActualValueKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenProgress(false);
    } else if (event.key === "Escape") {
      setOpenProgress(false);
    }
  };
  const handleCloseActualValue = (event) => {
    if (
      anchorRefActualValue.current &&
      anchorRefActualValue.current.contains(event.target)
    ) {
      return;
    }
    setOpenActualValue(false);
  };
  const handleClickActualValue = () => {
    setOpenActualValue((prevOpen) => !prevOpen);
  };
  const handleCheckboxActualValue = (val) => {
    let tmpCheckbox = [];
    if (val === "all") {
      if (
        checkboxActualValue.length >= 0 &&
        checkboxActualValue.length < checkboxActualValueList.length
      ) {
        checkboxActualValueList.forEach((actualValue) => {
          tmpCheckbox.push(actualValue);
        });
      } else {
        tmpCheckbox = [];
      }
    } else {
      if (checkboxActualValue.includes(val)) {
        tmpCheckbox = [...checkboxActualValue].filter(
          (actualValue) => actualValue !== val
        );
      } else {
        tmpCheckbox = [...checkboxActualValue, val];
      }
    }
    setCheckboxActualValue(tmpCheckbox);
  };

  // checkbox category
  const handleCategoryKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenProgress(false);
    } else if (event.key === "Escape") {
      setOpenProgress(false);
    }
  };
  const handleCloseCategory = (event) => {
    if (
      anchorRefCategory.current &&
      anchorRefCategory.current.contains(event.target)
    ) {
      return;
    }
    setOpenCategory(false);
  };
  const handleClickCategory = () => {
    setOpenCategory((prevOpen) => !prevOpen);
  };
  const handleCheckboxCategory = (val) => {
    let tmpCheckbox = [];
    if (val === "all") {
      if (
        checkboxCategory.length >= 0 &&
        checkboxCategory.length < checkboxCategoryList.length
      ) {
        checkboxCategoryList.forEach((category) => {
          tmpCheckbox.push(category.id);
        });
      }
    } else {
      if (checkboxCategory.includes(val)) {
        tmpCheckbox = [...checkboxCategory].filter(
          (category) => category !== val
        );
      } else {
        tmpCheckbox = [...checkboxCategory, val];
      }
    }
    setCheckboxCategory(tmpCheckbox);
  };

  const getGoalFormStatusData = () => {
    // goal form status
    dispatch(postGoalFormStatusEmployeeDashboard(goalFormStatusFilters)).then(
      (resp) => {
        if (resp?.employees) {
          setList(resp.employees);
          // set pagination
          setTotalPage(resp?.total_page ?? 1);
        }
      }
    );
  };

  const getGoalProgressData = (filters) => {
    let tmpGoalProgressFilters = JSON.parse(
      JSON.stringify(filters)
    );
    tmpGoalProgressFilters = {
      cycle_id: tmpGoalProgressFilters.cycle_id,
      goal_category_id: tmpGoalProgressFilters.goal_category_id,
      organization_id: tmpGoalProgressFilters.organization_id,
      progress_status: tmpGoalProgressFilters.progress_status,
      word: tmpGoalProgressFilters.word,
    };
    // goal form status
    dispatch(postGoalProgressEmployeeDashboard(tmpGoalProgressFilters)).then(
      (resp) => {
        if (resp?.table_header) {
          setGoalProgressHeaders(resp?.table_header);
        }
        if (resp?.employees) {
          setList(resp.employees);
        }
      }
    );
  };

  const getGoalProgressLogsData = (data) => {
    // goal form status
    dispatch(postGoalProgressLogDashboard(data?.form_id)).then((resp) => {
      if (resp?.logs) {
        let logs = resp?.logs?.map((log) => {
          return {
            ...log,
            created_at: log?.updated_at ?? "",
            name_th: log?.progress?.name ?? "",
            name_en: log?.progress?.name ?? "",
            color: log?.progress?.color,
          };
        });
        setHistoryData(logs);
      }
      let tmp = {
        goal_name: resp?.goal,
        target_value: data?.metric_value,
        target_condition: data?.metric_condition,
        type: data?.goal_metric?.toLowerCase() ?? "",
      };
      setHistoryOptionalData(tmp);
      handleHistoryDialog(true);
    });
  };

  const getSummaryData = () => {
    // summary
    dispatch(getGoalDashboard(filtersQuery)).then((resp) => {
      let summary = resp?.summary;
      let filter = resp?.filter;
      if (summary) {
        // set form status chart
        setFormGoalRawData(summary?.form_status);
        // set form status chart
        setProgressRawData(summary?.progress);
        // set achieved data
        setAchievedData(summary?.achieved);
        calcPercentage(summary?.achieved);
      }

      if (filter) {
        setCategoryData(filter?.goal_category);
      }
      dispatch({
        type: "SET_TOTAL_EMPLOYEE_DASHBOARD_MANAGER",
        payload: resp?.total?.employee ?? 0,
      });
    });
  };

  const handleChangeTableType = (_, val) => {
    if (val !== null) {
      setTableType(val);
    }
  };

  const handleSearch = (val, goalStatusFilters, goalProgressFilter) => {
    setSearch(val);
    doSearch(val, goalStatusFilters, goalProgressFilter);
  };
  const doSearch = useCallback(
    debounce((word, goalStatusFilters, goalProgressFilter) => {
      let formatGoalFormStatusFilters = {
        ...goalStatusFilters,
        ...{
          word: word,
        },
      };
      let formatGoalProgressFilters = {
        ...goalProgressFilter,
        ...{
          word: word,
        },
      };
      setGoalFormStatusFilters(formatGoalFormStatusFilters);
      setGoalProgressFilters(formatGoalProgressFilters);
    }, 2000),
    []
  );

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
  };

  const handleSetCurrentPage = (value) => {
    const getSelectedPage = value.filter(
      (item) => item.type === "page" && item.selected
    );
    setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1);
  };

  const sortData = (key, direction) => {
    setCurrentSort({ key, direction: direction ? "asc" : "desc" });
  };

  const formatFormGoalChart = (data) => {
    let name = [];
    let seriesData = [];
    let colors = [];
    let percentage = [];
    let chartData = data;
    if (chartData) {
      data.forEach((val) => {
        name.push(val.label ?? "-");
        seriesData.push(val.total ?? 0);
        colors.push(val.color);
        percentage = { ...percentage, [val.label]: val.percentage };
      });
    }
    setPercentageFormStatus(percentage);
    let totalForms = seriesData.reduce((prev, cur) => prev + cur, 0);
    setTotalStatusForms(totalForms);
    let tempChartData = {
      ...formGoalChart,
      ...{
        series: seriesData,
      },
    };
    tempChartData.options = {
      ...tempChartData.options,
      ...{
        labels: name,
        colors: colors,
      },
    };
    setFormGoalChartData(tempChartData);
  };

  const formatProgressChart = (data) => {
    let name = [];
    let seriesData = [];
    let colors = [];
    let chartData = data;
    let percentage = {};
    if (chartData) {
      data.forEach((val) => {
        name.push(val.label ?? "-");
        seriesData.push(val.total ?? 0);
        colors.push(val.color.font);
        percentage = { ...percentage, [val.label]: val.percentage };
      });
    }
    setPercentageProgress(percentage);
    let totalProgress = seriesData.reduce((prev, cur) => prev + cur, 0);
    setTotalProgress(totalProgress);
    let tempChartData = {
      ...progressChart,
      ...{
        series: seriesData,
      },
    };
    tempChartData.options = {
      ...tempChartData.options,
      ...{
        labels: name,
        colors: colors,
      },
    };
    setProgressChartData(tempChartData);
  };

  const statusLabel = (status) => {
    if (status === "draft" || status === "In Progress") {
      return i18n.language === "en-US" ? "In Progress" : "กำลังดำเนินการ";
    } else if (status === "pending" || status === "Pending") {
      return i18n.language === "en-US" ? "Pending" : "รอดำเนินการ";
    } else if (status === "approved" || status === "Approved") {
      return i18n.language === "en-US" ? "Approved" : "ได้รับการอนุมัติ";
    }
  };

  const chartLegend = (data, type = "form_status") => {
    let seriesData = data?.series;
    let percentSeriesData = seriesData.map((_, index) => {
      return type === "form_status"
        ? parseFloat(percentageFormStatus[data?.options?.labels[index]])
        : parseFloat(percentageProgress[data?.options?.labels[index]]);
    });
    let labels = data?.options?.labels;
    let colors = data?.options?.colors;

    const PreDom = (props) => {
      let count = 12;
      return (
        <Grid item xs={count}>
          {props.children}
        </Grid>
      );
    };
    let resultDom = null;
    let listDom = [];
    if (seriesData && labels && colors) {
      seriesData.forEach((series, index) => {
        listDom.push(
          <Grid
            container
            key={`series-${index}`}
            mb={index < seriesData.length - 1 ? "10px" : "0"}
          >
            <Grid item xs="auto" display="flex" alignItems="center">
              <Box
                sx={{
                  borderRadius: "50%",
                  width: "10px",
                  height: "10px",
                  background: colors?.[index] ?? "#EAEAEA",
                  mr: 2,
                }}
              />
              <Typography fontSize="16px" fontWeight="400" color="#AAAAAA">
                {labels?.[index]
                  ? type === "form_status"
                    ? Util.upperFirstWord(statusLabel(labels?.[index]))
                    : labels?.[index]
                  : "-"}
              </Typography>
            </Grid>
            <Grid item ml="auto" display="flex" alignItems="center">
              <Typography fontSize="16px" fontWeight="400" color="#AAAAAA">
                {series} (
                {!isNaN(percentSeriesData?.[index]?.toFixed(2))
                  ? percentSeriesData?.[index]?.toFixed(2)
                  : "0.00"}
                %)
              </Typography>
            </Grid>
          </Grid>
        );
      });
    }
    resultDom = <PreDom>{listDom}</PreDom>;

    return resultDom;
  };

  const statusBox = (data, isCenter = false) => {
    let colors = {
      inProgress: {
        font: "#EFB76D",
        background: "rgba(239, 183, 109, 0.1)",
      },
      completed: {
        font: "#5DA196",
        background: "rgba(93, 161, 150, 0.1)",
      },
      pending: {
        font: "#F28E2B",
        background: "rgba(242, 142, 43, 0.1)",
      },
    };

    const statusKey = (status) => {
      if (status === "draft") {
        return "inProgress";
      } else if (status === "pending") {
        return "pending";
      } else if (status === "approved") {
        return "completed";
      }
    };

    return (
      <Box
        sx={{
          backgroundColor: colors?.[statusKey(data)]?.background ?? "#fff",
          width: "120px",
          paddingTop: "8px",
          paddingBottom: "8px",
          height: "40px",
          textAlign: "center",
          borderRadius: "5px",
          margin: isCenter ? "0 auto" : "none",
        }}
      >
        <Typography
          fontSize={16}
          lineHeight="21px"
          fontWeight="500"
          color={colors?.[statusKey(data)]?.font ?? "#404349"}
        >
          {Util.upperFirstWord(statusLabel(data) ?? "-")}
        </Typography>
      </Box>
    );
  };

  const statusBoxProgress = (data, isCenter = false) => {
    const getColors = () => {
      let findProgress = checkboxProgressList?.find(
        (progress) => progress.label === data
      );
      return findProgress;
    };
    return (
      <Box
        sx={{
          backgroundColor: getColors(data)?.color?.background ?? "#fff",
          minWidth: "120px",
          paddingTop: "8px",
          paddingBottom: "8px",
          height: "40px",
          textAlign: "center",
          borderRadius: "5px",
          margin: isCenter ? "0 auto" : "none",
          border:
            data === "Not Started" || data === "ยังไม่ได้ดำเนินการ"
              ? "1px solid #DADADA4A"
              : `0px`,
        }}
      >
        <Typography
          fontSize={16}
          lineHeight="21px"
          fontWeight="500"
          color={getColors(data)?.color?.font ?? "#404349"}
        >
          {Util.upperFirstWord(data ?? "-")}
        </Typography>
      </Box>
    );
  };

  const calcPercentage = (data) => {
    let result = [];
    if (data && data?.length > 0) {
      let total = 0;
      data.forEach((item) => {
        total += item.total;
      });
      let goalStatus = parseInt(data?.[0]?.total ?? 0);
      let progress = parseInt(data?.[1]?.total ?? 0);

      goalStatus = (goalStatus / total) * 100;
      progress = 100 - goalStatus;
      result = [goalStatus, progress];
    }
    setBarPercentage(result);
  };

  const doSetProgressCheckbox = (data) => {
    let tmpList = [];
    data.forEach((progress) => {
      tmpList.push(progress?.id);
    });
    setCheckboxProgressList(data);
    setCheckboxProgress(tmpList);
    setFirstLoaded({
      ...firstLoaded,
      progress: true
    })
  };

  const doSetCategoryCheckbox = (data) => {
    let tmpList = [];
    data.forEach((category) => {
      tmpList.push(category.id);
    });
    setCheckboxCategoryList(data);
    setCheckboxCategory(tmpList);
    setFirstLoaded({
      ...firstLoaded,
      category: true
    })
  };

  const getMetricConditionTextByLang = (word = "") => {
    let isEng = i18n.language !== "th-TH" ? true : false;
    let result = word;
    word = word.toLowerCase();
    switch (word) {
      case "equal":
        result = isEng ? "Equal to" : "เท่ากับ";
        break;
      case "higher or equal to":
        result = isEng ? "Higher or Equal to" : "มากกว่าหรือเท่ากับ";
        break;
      case "less or equal to":
        result = isEng ? "Less or Equal to" : "น้อยกว่าหรือเท่ากับ";
        break;
      case "higher":
        result = isEng ? "Higher than" : "มากกว่า";
        break;
      case "less":
        result = isEng ? "Less than" : "น้อยกว่า";
        break;
      default:
        break;
    }
    return result;
  };

  const validateActualValueColor = (data) => {
    let result = false;
    let targetCondition = "";
    let conditionData = {
      target_condition: data?.metric_condition ?? "",
      target_value: data?.metric_value,
      type: data?.goal_metric?.toLocaleLowerCase() ?? "date",
    };
    if (
      conditionData?.target_condition &&
      conditionData?.target_condition !== ""
    ) {
      targetCondition = conditionData.target_condition.toLowerCase();
    }
    if (
      conditionData?.type === "value" ||
      conditionData?.type === "ทำได้ตัวเลขตามเป้าหมาย"
    ) {
      if (targetCondition === "equal") {
        if (
          parseFloat(conditionData?.target_value) ===
          parseFloat(data.actual_value)
        ) {
          result = true;
        }
      } else if (targetCondition === "higher or equal to") {
        if (
          parseFloat(conditionData?.target_value) <=
          parseFloat(data.actual_value)
        ) {
          result = true;
        }
      } else if (targetCondition === "less or equal to") {
        if (
          parseFloat(conditionData?.target_value) >=
          parseFloat(data.actual_value)
        ) {
          result = true;
        }
      } else if (targetCondition === "higher") {
        if (
          parseFloat(conditionData?.target_value) <
          parseFloat(data.actual_value)
        ) {
          result = true;
        }
      } else if (targetCondition === "less") {
        if (
          parseFloat(conditionData?.target_value) >
          parseFloat(data.actual_value)
        ) {
          result = true;
        }
      }
    } else {
      if (
        conditionData?.type === "accomplishment" ||
        conditionData?.type === "ทำสำเร็จหรือไม่"
      ) {
        if (
          conditionData?.target_value?.toLowerCase() ===
          data.actual_value?.toLowerCase()
        ) {
          result = true;
        }
      } else if (
        conditionData?.type === "date" ||
        conditionData?.type === "ทำได้ภายใน End Date"
      ) {
        let targetValue = moment(
          String(conditionData?.target_value),
          "DD-MM-YYYY"
        );
        let actualValue = moment(String(data.actual_value), "DD-MM-YYYY");
        let diff = targetValue.diff(actualValue, "days");
        if (diff >= 0) {
          result = true;
        }
      } else {
        if (conditionData?.target_value >= data.actual_value) {
          result = true;
        }
      }
    }
    return result;
  };

  const handleTotalPage = () => {
    const List =
      filteredList.length > 0 ? JSON.parse(JSON.stringify(filteredList)) : [];
    const totalPage =
      List.length > 0
        ? Math.ceil(List.length / perPage) === 0
          ? 1
          : Math.ceil(List.length / perPage)
        : 1;
    setTotalPage(totalPage);
  };

  const currentData = useMemo(() => {
    if (tableType === "form_status") {
      return filteredList;
    } else {
      const firstPageIndex = (currentPage - 1) * perPage;
      const lastPageIndex = firstPageIndex + perPage;
      let result = filteredList.filter((item) => {
        if (checkboxActualValue.every((item) => item == "Achieved")) {
          return item.is_achieved === "yes";
        } else if (
          checkboxActualValue.every((item) => item == "Not Achieved")
        ) {
          return item.is_achieved === "no";
        } else {
          return item;
        }
      });
      const totalPage =
        result.length > 0
          ? Math.ceil(result.length / perPage) === 0
            ? 1
            : Math.ceil(result.length / perPage)
          : 1;
      setTotalPage(totalPage);
      result = result.slice(firstPageIndex, lastPageIndex);
      return result;
    }
  }, [filteredList, tableType, checkboxActualValue, currentPage, perPage]);

  const getDataByTableType = (type) => {
    if (type === "form_status") {
      if (
        goalFormStatusFilters.cycle_id !== "" &&
        goalFormStatusFilters.organization_id !== ""
      ) {
        getGoalFormStatusData();
      }
    } else if (type === "progress") {
      let filters = {
        ...goalProgressFilters,
      };

      if (checkboxCategory.length > 0) {
        filters = {
          ...filters,
          ...{
            goal_category_id: checkboxCategory.join(","),
          },
        };
      } else if (checkboxCategory.length === 0) {
        delete filters["goal_category_id"];
      }

      if (checkboxProgress.length > 0) {
        filters = {
          ...filters,
          ...{
            progress_status: checkboxProgress.join(","),
          },
        };
      }

      if (
        filters.cycle_id !== "" &&
        filters.organization_id !== ""
      ) {
        getGoalProgressData(filters);
      }
    }
  }

  // useEffect
  useEffect(() => {
    setFirstLoaded({ progress: false, category: false })
    let query = [];
    if (
      managerDashboardState?.filtersChecked &&
      managerDashboardState?.cycleData?.serial &&
      managerDashboardState?.cycleData?.serial !== ""
    ) {
      let reqData = {
        organization_id: [],
        cycle_id: "",
      };
      let orgIds = managerDashboardState?.filtersChecked?.map((filter) => {
        return `${filter}`;
      });
      if (orgIds.length > 0) {
        let tmpQuery = orgIds.join(",");

        let formatGoalFormStatusFilters = {
          ...goalFormStatusFilters,
          ...{
            cycle_id: managerDashboardState.cycleData.serial,
            organization_id: tmpQuery,
            form_status: checkboxStatus.join(","),
          },
        };
        let formatGoalProgressFilters = {
          ...goalProgressFilters,
          ...{
            cycle_id: managerDashboardState.cycleData.serial,
            organization_id: tmpQuery,
            form_status: checkboxStatus.join(","),
          },
        };

        if (perPage && currentPage) {
          if (tableType === "form_status") {
            formatGoalFormStatusFilters = {
              ...formatGoalFormStatusFilters,
              ...{
                limit: perPage ?? 1,
                page: currentPage ?? 1,
              },
            };
            if (currentSort.key !== "" && currentSort.direction !== "") {
              formatGoalFormStatusFilters = {
                ...formatGoalFormStatusFilters,
                ...{
                  order_by: `${currentSort.key},${currentSort.direction}`,
                },
              };
            }
          } else {
            formatGoalProgressFilters = {
              ...formatGoalProgressFilters,
              ...{
                limit: perPage ?? 1,
                page: currentPage ?? 1,
              },
            };
            if (currentSort.key !== "" && currentSort.direction !== "") {
              formatGoalProgressFilters = {
                ...formatGoalProgressFilters,
                ...{
                  order_by: `${currentSort.key},${currentSort.direction}`,
                },
              };
            }
          }
        }

        if (tableType === "progress") {
          setGoalProgressFilters(formatGoalProgressFilters);
        } else {
          setGoalFormStatusFilters(formatGoalFormStatusFilters);
        }
      }
      reqData.organization_id = orgIds;
      reqData.cycle_id = managerDashboardState.cycleData.serial;
      setCurrentCycle(managerDashboardState.cycleData);
      setFiltersQuery({ query, reqData });
    }
  }, [
    managerDashboardState.filtersChecked,
    checkboxStatus,
    perPage,
    currentPage,
    currentSort,
    tableType
  ]);

  useEffect(() => {
    if (
      filtersQuery?.reqData?.cycle_id !== "" &&
      filtersQuery?.reqData?.organization_id?.length > 0
    )
      getSummaryData();
  }, [filtersQuery]);

  useEffect(() => {
    if (
      tableType === "progress" &&
      goalProgressFilters.cycle_id !== "" && goalProgressFilters.organization_id !== "" &&
      checkboxProgressList.length > 0 &&
      checkboxCategoryList.length > 0 &&
      firstLoaded.category && firstLoaded.progress
    )
      getDataByTableType("progress")
  }, [goalProgressFilters, checkboxCategoryList, checkboxCategory, checkboxProgressList, checkboxProgress, firstLoaded]);

  useEffect(() => {
    if (
      tableType === "form_status" &&
      goalFormStatusFilters.cycle_id !== "" && goalFormStatusFilters.organization_id !== ""
    )
      getDataByTableType("form_status")
  }, [goalFormStatusFilters]);

  useEffect(() => {
    if (formGoalRaw !== 0 && currentCycle !== {}) {
      formatFormGoalChart(formGoalRaw);
    }
  }, [formGoalRaw, currentCycle]);

  useEffect(() => {
    if (progressRaw !== 0 && currentCycle !== {}) {
      formatProgressChart(progressRaw);
      // set progress checkbox
      doSetProgressCheckbox(progressRaw);
    }
  }, [progressRaw, currentCycle]);

  useEffect(() => {
    if (categoryData !== 0 && currentCycle !== {}) {
      // set category checkbox
      doSetCategoryCheckbox(categoryData);
    }
  }, [categoryData, currentCycle]);

  useEffect(() => {
    setFilteredList(list);
  }, [list]);
  // end useEffect

  return (
    <>
      <Box id="back-to-top-anchor"> </Box>
      <Grid container columnSpacing={4} my={6}>
        <Grid item xs={12} md={6}>
          <Paper
            classes={{ root: classes.flexPaper }}
            sx={{
              minHeight: "530px",
              p: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid container>
              <Grid
                item
                xs="auto"
                display="flex"
                alignItems="center"
                textAlign="left"
              >
                <Typography fontSize={24} fontWeight="600">
                  {t("dashboardManager.label.goalFormStatus")}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              mt={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              m="auto 0"
            >
              <Grid
                item
                xs={6}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  sx={{
                    minHeight: "300px",
                    position: "relative",
                    ".apexcharts-tooltip-marker": { borderRadius: "2px" },
                    ".apexcharts-tooltip-y-group > span": {
                      fontWeight: "400",
                    },
                    ".apexcharts-tooltip": {
                      background: "#F7F7F7",
                      fontWeight: "400",
                    },
                    ".apexcharts-legend-text": {
                      color: "#A3A3A3 !important",
                    },
                    ".apexcharts-text": {
                      color: "#A3A3A3 !important",
                      fill: "#A3A3A3  !important",
                    },
                    ".apexcharts-tooltip-title": {
                      background: "#5DA196 !important",
                      color: "#FFFFFF",
                      fontWeight: "400",
                    },
                    height: "250px",
                  }}
                >
                  <ApexCharts
                    options={formGoalChart.options}
                    series={formGoalChart.series}
                    type="donut"
                    height="100%"
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -70%)",
                    }}
                  >
                    <Typography
                      fontSize="48px"
                      fontWeight="600"
                      textAlign="center"
                    >
                      {totalStatusForms}
                    </Typography>
                    <Typography
                      fontSize="16px"
                      fontWeight="400"
                      color="#AAAAAA"
                    >
                      {t("dashboardManager.label.totalForms")}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                xs={6}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {chartLegend(formGoalChart)}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            classes={{ root: classes.flexPaper }}
            sx={{
              minHeight: "530px",
              p: "20px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                width: "235px",
                height: "60px",
                border: "1px solid #DADADA",
                borderRadius: "5px",
                m: "20px",
                p: "12px 18px",
              }}
            >
              {!isNaN(barPercentage?.[0]) && !isNaN(barPercentage?.[1]) ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BootstrapTooltip
                    title={`${achievedData?.[0]?.label ?? "-"} ${achievedData?.[0]?.total ?? 0
                      } ${t("dashboardManager.label.goals").toLocaleLowerCase()}`}
                    placement="top"
                  >
                    <Box
                      sx={{
                        width: `${!isNaN(barPercentage?.[0]) ? barPercentage?.[0] : 0
                          }%`,
                        background: "#5DA196",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="#FFF" fontSize="10px">
                        {!isNaN(barPercentage?.[0])
                          ? barPercentage?.[0]?.toFixed(2)
                          : 0}
                        %
                      </Typography>
                    </Box>
                  </BootstrapTooltip>
                  <BootstrapTooltip
                    title={`${achievedData?.[1]?.label ?? "-"} ${achievedData?.[1]?.total ?? 0
                      } ${t("dashboardManager.label.goals").toLocaleLowerCase()}`}
                    placement="top"
                  >
                    <Box
                      sx={{
                        width: `${!isNaN(barPercentage?.[1]) ? barPercentage?.[1] : 0
                          }%`,
                        background: "#404349",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="#FFF" fontSize="10px">
                        {!isNaN(barPercentage?.[1])
                          ? barPercentage?.[1].toFixed(2)
                          : 0}
                        %
                      </Typography>
                    </Box>
                  </BootstrapTooltip>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: `100%`,
                      background: "#DADADA",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="#FFF" fontSize="10px">
                      -
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: "50%" }}>
                  <Typography color="#AAAAAA" fontSize="11px">
                    {achievedData?.[0]?.label ?? "-"}
                  </Typography>
                </Box>
                <Box sx={{ width: "50%", textAlign: "right" }}>
                  <Typography color="#AAAAAA" fontSize="11px">
                    {achievedData?.[1]?.label ?? "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Grid container>
              <Grid item display="flex" alignItems="center" textAlign="left">
                <Typography fontSize={24} fontWeight="600">
                  {t("dashboardManager.label.goalProgress")}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              mt={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              m="auto 0"
            >
              <Grid
                item
                xs={6}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  sx={{
                    minHeight: "300px",
                    position: "relative",
                    ".apexcharts-tooltip-marker": { borderRadius: "2px" },
                    ".apexcharts-tooltip-y-group > span": {
                      fontWeight: "400",
                    },
                    ".apexcharts-tooltip": {
                      background: "#F7F7F7",
                      fontWeight: "400",
                    },
                    ".apexcharts-legend-text": {
                      color: "#A3A3A3 !important",
                    },
                    ".apexcharts-text": {
                      color: "#A3A3A3 !important",
                      fill: "#A3A3A3  !important",
                    },
                    ".apexcharts-tooltip-title": {
                      background: "#5DA196 !important",
                      color: "#FFFFFF",
                      fontWeight: "400",
                    },
                  }}
                >
                  <ApexCharts
                    options={progressChart.options}
                    series={progressChart.series}
                    type="donut"
                    height="100%"
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -70%)",
                    }}
                  >
                    <Typography
                      fontSize="48px"
                      fontWeight="600"
                      textAlign="center"
                    >
                      {totalProgress}
                    </Typography>
                    <Typography
                      fontSize="16px"
                      fontWeight="400"
                      color="#AAAAAA"
                    >
                      {t("dashboardManager.label.totalGoals")}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                xs={6}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {chartLegend(progressChart, "progress")}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Grid container mb={0}>
        <Grid item xs>
          <ToggleButtonGroup
            color="primary"
            value={tableType}
            exclusive
            onChange={handleChangeTableType}
            aria-label="Goal Form Status Data Table"
          >
            <ToggleButton
              value="form_status"
              sx={{
                minWidth: "177px",
                height: "45px",
                backgroundColor: "#FFF",
                textTransform: "none",
                borderRadius: "5px 0px 0px 0px",
              }}
              classes={{ selected: classes.toggleBtnSelected }}
            >
              <Typography
                fontSize="16px"
                fontWeight={tableType === "form_status" ? "500" : "400"}
                color={tableType === "form_status" ? "#FFF" : "#AAAAAA"}
              >
                {t("dashboardManager.label.goalFormStatus")}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="progress"
              sx={{
                minWidth: "177px",
                height: "45px",
                backgroundColor: "#FFF",
                textTransform: "none",
                borderRadius: "0px 5px 0px 0px",
              }}
              classes={{ selected: classes.toggleBtnSelected }}
            >
              <Typography
                fontSize="16px"
                fontWeight={tableType === "progress" ? "500" : "400"}
                color={tableType === "progress" ? "#FFF" : "#AAAAAA"}
              >
                {t("dashboardManager.label.goalProgress")}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      {/* TABLE */}
      <Grid container spacing={2}>
        <Grid item xs>
          <Box className={classes.paperRoot}>
            <Paper className={classes.flexPaper}>
              <Grid container spacing={2} px="20px" pt="20px" pb="30px">
                <Grid item>
                  <Typography fontSize={24} fontWeight="600">
                    {tableType === "form_status"
                      ? t("dashboardManager.label.goalFormStatusDetail")
                      : t("dashboardManager.label.goalProgress")}
                  </Typography>
                </Grid>
                <Grid item ml="auto">
                  <TextField
                    id="search-table"
                    fullWidth
                    placeholder={t(
                      "dashboardManager.label.searchPlaceholderEvaluation1"
                    )}
                    size="small"
                    sx={{
                      background: "#fff",
                      width: "400px",
                      height: "45px",
                    }}
                    value={search}
                    onChange={(e) =>
                      handleSearch(
                        e.target.value,
                        goalFormStatusFilters,
                        goalProgressFilters
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search style={{ color: "#C4C4C4" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              {/* TABLE HEADER */}
              {tableType === "form_status" ? (
                <Box px="20px">
                  <Box
                    px="25px"
                    py="19px"
                    sx={{ display: "flex", flexDirection: "row" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.formStatus[0],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={16}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.employee")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_name", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_name", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.formStatus[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={16}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.formName")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      ></Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("form_name", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("form_name", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.formStatus[2],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={16}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.startDate")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("start_at", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("start_at", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.formStatus[3],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={16}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.endDate")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("end_at", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("end_at", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.formStatus[4],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={16}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.status")}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <div>
                          <FilterStatusIcon
                            ref={anchorRefStatus}
                            id="status-filter-button"
                            style={{ cursor: "pointer" }}
                            onClick={handleClickStatus}
                          />
                          <Popper
                            open={openStatus}
                            anchorEl={anchorRefStatus.current}
                            role={undefined}
                            placement="bottom-start"
                            transition
                            disablePortal
                            style={{ zIndex: "1" }}
                          >
                            {({ TransitionProps, placement }) => (
                              <Grow
                                {...TransitionProps}
                                style={{
                                  transformOrigin:
                                    placement === "bottom-start"
                                      ? "left top"
                                      : "left bottom",
                                }}
                              >
                                <Paper sx={{ mt: "5px !important" }}>
                                  <ClickAwayListener
                                    onClickAway={handleCloseStatus}
                                  >
                                    <MenuList
                                      autoFocusItem={openStatus}
                                      id="status-menu"
                                      aria-labelledby="composition-button"
                                      onKeyDown={handleStatusKeyDown}
                                    >
                                      <Box px="16px" py="6px">
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <CustomCheckbox
                                                indeterminate={
                                                  checkboxStatus.length > 0 &&
                                                  checkboxStatus.length < 3
                                                }
                                                onChange={() =>
                                                  handleCheckboxStatus("all")
                                                }
                                                checked={
                                                  checkboxStatus.length === 3
                                                }
                                              />
                                            }
                                            label={t("label.selectAll")}
                                          />
                                          {checkboxStatusList &&
                                            checkboxStatusList.map((item) => {
                                              return (
                                                <FormControlLabel
                                                  control={
                                                    <CustomCheckbox
                                                      onChange={() =>
                                                        handleCheckboxStatus(
                                                          item
                                                        )
                                                      }
                                                      checked={checkboxStatus.includes(
                                                        item
                                                      )}
                                                    />
                                                  }
                                                  label={
                                                    item === "draft"
                                                      ? t(
                                                        "dashboardManager.label.inProgress"
                                                      )
                                                      : item === "approved"
                                                        ? t(
                                                          "dashboardManager.label.approved"
                                                        )
                                                        : item === "pending"
                                                          ? t(
                                                            "dashboardManager.label.pending"
                                                          )
                                                          : "-"
                                                  }
                                                  key={`filters-${item}`}
                                                />
                                              );
                                            })}
                                        </FormGroup>
                                      </Box>
                                    </MenuList>
                                  </ClickAwayListener>
                                </Paper>
                              </Grow>
                            )}
                          </Popper>
                        </div>
                      </Stack>
                    </Box>
                  </Box>
                  <Divider width="100%" />
                </Box>
              ) : (
                <Box px="20px">
                  <Box
                    px="25px"
                    py="19px"
                    sx={{ display: "flex", flexDirection: "row" }}
                  >
                    {goalProgressHeaders.map((header) => {
                      return (
                        <>
                          {header.column === "employee" &&
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: columnWidths.progress[0],
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                color="#A0A0A0"
                                fontWeight="500"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "max-content",
                                  mr: "10px",
                                }}
                              >
                                {t("dashboardManager.label.employeeFullName")}
                              </Typography>
                              <Typography
                                component="span"
                                style={{
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  display: "flex",
                                }}
                              >
                                <ChevronExpandUp
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("employee_name", true)}
                                />
                                <ChevronExpandDown
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) =>
                                    sortData("employee_name", false)
                                  }
                                />
                              </Typography>
                            </Box>}
                          {header.column === "goal_name" &&
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: columnWidths.progress[1],
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                color="#A0A0A0"
                                fontWeight="500"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "max-content",
                                  mr: "10px",
                                }}
                              >
                                {t("dashboardManager.label.goalName")}
                              </Typography>
                              <Typography
                                component="span"
                                style={{
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  display: "flex",
                                }}
                              ></Typography>
                              <Typography
                                component="span"
                                style={{
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  display: "flex",
                                }}
                              >
                                <ChevronExpandUp
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("goal_name", true)}
                                />
                                <ChevronExpandDown
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("goal_name", false)}
                                />
                              </Typography>
                            </Box>}
                          {(header.column === "form_custom" || header.column === "category") &&
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: columnWidths.progress[2],
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                color="#A0A0A0"
                                fontWeight="500"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "max-content",
                                  mr: "10px",
                                }}
                              >
                                {header.custom ? header.label && header.label !== "" ? header.label : t("dashboardManager.label.goalCategory") : t("dashboardManager.label.goalCategory")}
                              </Typography>
                              <Stack direction="row" spacing={2}>
                                <div>
                                  <FilterStatusIcon
                                    ref={anchorRefCategory}
                                    id="progress-filter-button"
                                    style={{ cursor: "pointer" }}
                                    onClick={handleClickCategory}
                                  />
                                  <Popper
                                    open={openCategory}
                                    anchorEl={anchorRefCategory.current}
                                    role={undefined}
                                    placement="bottom-start"
                                    transition
                                    disablePortal
                                    style={{ zIndex: "1" }}
                                  >
                                    {({ TransitionProps, placement }) => (
                                      <Grow
                                        {...TransitionProps}
                                        style={{
                                          transformOrigin:
                                            placement === "bottom-start"
                                              ? "left top"
                                              : "left bottom",
                                        }}
                                      >
                                        <Paper sx={{ mt: "5px !important" }}>
                                          <ClickAwayListener
                                            onClickAway={handleCloseCategory}
                                          >
                                            <MenuList
                                              autoFocusItem={openCategory}
                                              id="status-menu"
                                              aria-labelledby="composition-button"
                                              onKeyDown={handleCategoryKeyDown}
                                            >
                                              <Box px="16px" py="6px">
                                                <FormGroup>
                                                  <FormControlLabel
                                                    control={
                                                      <CustomCheckbox
                                                        indeterminate={
                                                          checkboxCategory.length > 0 &&
                                                          checkboxCategory.length <
                                                          checkboxCategoryList.length
                                                        }
                                                        onChange={() =>
                                                          handleCheckboxCategory("all")
                                                        }
                                                        checked={
                                                          checkboxCategory.length ===
                                                          checkboxCategoryList.length
                                                        }
                                                      />
                                                    }
                                                    label={t("label.selectAll")}
                                                  />
                                                  {checkboxCategoryList &&
                                                    checkboxCategoryList.map((item) => {
                                                      return (
                                                        <FormControlLabel
                                                          control={
                                                            <CustomCheckbox
                                                              onChange={() =>
                                                                handleCheckboxCategory(
                                                                  item.id
                                                                )
                                                              }
                                                              checked={checkboxCategory.includes(
                                                                item.id
                                                              )}
                                                            />
                                                          }
                                                          label={item.name}
                                                          key={`filters-category-${item.name}`}
                                                        />
                                                      );
                                                    })}
                                                </FormGroup>
                                              </Box>
                                            </MenuList>
                                          </ClickAwayListener>
                                        </Paper>
                                      </Grow>
                                    )}
                                  </Popper>
                                </div>
                              </Stack>
                            </Box>}
                          {header.column === "goal_start_at" &&
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: columnWidths.progress[3],
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                color="#A0A0A0"
                                fontWeight="500"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "max-content",
                                  mr: "10px",
                                }}
                              >
                                {t("dashboardManager.label.startDate")}
                              </Typography>
                              <Typography
                                component="span"
                                style={{
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  display: "flex",
                                }}
                              >
                                <ChevronExpandUp
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("goal_start_at", true)}
                                />
                                <ChevronExpandDown
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("goal_start_at", false)}
                                />
                              </Typography>
                            </Box>
                          }
                          {header.column === "goal_end_at" &&
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: columnWidths.progress[4],
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                color="#A0A0A0"
                                fontWeight="500"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "max-content",
                                  mr: "10px",
                                }}
                              >
                                {t("dashboardManager.label.endDate")}
                              </Typography>
                              <Typography
                                component="span"
                                style={{
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  display: "flex",
                                }}
                              >
                                <ChevronExpandUp
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("goal_end_at", true)}
                                />
                                <ChevronExpandDown
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => sortData("goal_end_at", false)}
                                />
                              </Typography>
                            </Box>
                          }
                          {header.column === "actual_value" &&
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: columnWidths.progress[5],
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                color="#A0A0A0"
                                fontWeight="500"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "max-content",
                                  mr: "10px",
                                }}
                              >
                                {t("dashboardManager.label.actualValue")}
                              </Typography>
                              <Stack direction="row" spacing={2}>
                                <div>
                                  <FilterStatusIcon
                                    ref={anchorRefActualValue}
                                    id="progress-filter-button"
                                    style={{ cursor: "pointer" }}
                                    onClick={handleClickActualValue}
                                  />
                                  <Popper
                                    open={openActualValue}
                                    anchorEl={anchorRefActualValue.current}
                                    role={undefined}
                                    placement="bottom-start"
                                    transition
                                    disablePortal
                                    style={{ zIndex: "1" }}
                                  >
                                    {({ TransitionProps, placement }) => (
                                      <Grow
                                        {...TransitionProps}
                                        style={{
                                          transformOrigin:
                                            placement === "bottom-start"
                                              ? "left top"
                                              : "left bottom",
                                        }}
                                      >
                                        <Paper sx={{ mt: "5px !important" }}>
                                          <ClickAwayListener
                                            onClickAway={handleCloseActualValue}
                                          >
                                            <MenuList
                                              autoFocusItem={openActualValue}
                                              id="status-menu"
                                              aria-labelledby="composition-button"
                                              onKeyDown={handleActualValueKeyDown}
                                            >
                                              <Box px="16px" py="6px">
                                                <FormGroup>
                                                  {checkboxActualValueList &&
                                                    checkboxActualValueList.map(
                                                      (item) => {
                                                        return (
                                                          <FormControlLabel
                                                            control={
                                                              <CustomCheckbox
                                                                onChange={() =>
                                                                  handleCheckboxActualValue(
                                                                    item
                                                                  )
                                                                }
                                                                checked={checkboxActualValue.includes(
                                                                  item
                                                                )}
                                                              />
                                                            }
                                                            label={item}
                                                            key={`filters-ActualValue-${item}`}
                                                          />
                                                        );
                                                      }
                                                    )}
                                                </FormGroup>
                                              </Box>
                                            </MenuList>
                                          </ClickAwayListener>
                                        </Paper>
                                      </Grow>
                                    )}
                                  </Popper>
                                </div>
                              </Stack>
                            </Box>
                          }
                        </>
                      );
                    })}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.progress[6],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={16}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.progress")}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <div>
                          <FilterStatusIcon
                            ref={anchorRefProgress}
                            id="progress-filter-button"
                            style={{ cursor: "pointer" }}
                            onClick={handleClickProgress}
                          />
                          <Popper
                            open={openProgress}
                            anchorEl={anchorRefProgress.current}
                            role={undefined}
                            placement="bottom-start"
                            transition
                            disablePortal
                            style={{ zIndex: "1" }}
                          >
                            {({ TransitionProps, placement }) => (
                              <Grow
                                {...TransitionProps}
                                style={{
                                  transformOrigin:
                                    placement === "bottom-start"
                                      ? "left top"
                                      : "left bottom",
                                }}
                              >
                                <Paper sx={{ mt: "5px !important" }}>
                                  <ClickAwayListener
                                    onClickAway={handleCloseProgress}
                                  >
                                    <MenuList
                                      autoFocusItem={openProgress}
                                      id="status-menu"
                                      aria-labelledby="composition-button"
                                      onKeyDown={handleProgressKeyDown}
                                    >
                                      <Box px="16px" py="6px">
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <CustomCheckbox
                                                indeterminate={
                                                  checkboxProgress.length > 0 &&
                                                  checkboxProgress.length <
                                                  checkboxProgressList.length
                                                }
                                                onChange={() =>
                                                  handleCheckboxProgress("all")
                                                }
                                                checked={
                                                  checkboxProgress.length ===
                                                  checkboxProgressList.length
                                                }
                                              />
                                            }
                                            label={t("label.selectAll")}
                                          />
                                          {checkboxProgressList &&
                                            checkboxProgressList.map((item) => {
                                              return (
                                                <FormControlLabel
                                                  control={
                                                    <CustomCheckbox
                                                      onChange={() =>
                                                        handleCheckboxProgress(
                                                          item.id
                                                        )
                                                      }
                                                      checked={checkboxProgress.includes(
                                                        item.id
                                                      )}
                                                    />
                                                  }
                                                  label={item.label}
                                                  key={`filters-progress-${item.label}`}
                                                />
                                              );
                                            })}
                                        </FormGroup>
                                      </Box>
                                    </MenuList>
                                  </ClickAwayListener>
                                </Paper>
                              </Grow>
                            )}
                          </Popper>
                        </div>
                      </Stack>
                    </Box>
                  </Box>
                  <Divider width="100%" />
                </Box>
              )}
              {/* TABLE DATA */}
              {currentData && currentData.length > 0 ? (
                currentData.map((row, index) => {
                  if (tableType === "form_status") {
                    return (
                      <Box px="20px" key={`emp-${index}`}>
                        <Box
                          sx={{
                            width: "100%",
                            position: "relative",
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              height: "96px",
                              background: "#FFF",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                            px="25px"
                          >
                            <Box
                              sx={{ width: columnWidths.formStatus[0] }}
                              pr="15px"
                            >
                              <Grid container spacing={2} flexWrap="nowrap">
                                <Grid item display="flex" alignItems="center">
                                  <Box sx={{ position: "relative" }}>
                                    <Avatar
                                      src={row?.employee_photo}
                                      sx={{
                                        border:
                                          row?.employee_submited === "y"
                                            ? "3px solid #5DA196"
                                            : "none",
                                      }}
                                    />
                                  </Box>
                                </Grid>
                                <Grid item>
                                  <Grid container>
                                    <Grid
                                      item
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        fontSize="16px"
                                        fontWeight="500"
                                        sx={{
                                          WebkitBoxOrient: "vertical",
                                          WebkitLineClamp: 2,
                                          display: "-webkit-box",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {row?.employee_name ?? "-"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid container>
                                    <Grid
                                      item
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        fontSize="16px"
                                        fontWeight="400"
                                        color="#AAAAAA"
                                        sx={{
                                          WebkitBoxOrient: "vertical",
                                          WebkitLineClamp: 2,
                                          display: "-webkit-box",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {row?.employee_position ?? "-"} (
                                        {row?.employee_organization ?? "-"})
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>
                            <Box
                              sx={{ width: columnWidths.formStatus[1] }}
                              pr="15px"
                            >
                              <Grid container spacing={2} flexWrap="nowrap">
                                <Grid item display="flex" alignItems="center">
                                  <Typography fontSize="16px" fontWeight="400">
                                    {row?.form_name ?? "-"}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                            <Box
                              sx={{
                                width: columnWidths.formStatus[2],
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                              pr="15px"
                            >
                              <Typography fontWeight="400" fontSize="16px">
                                {Util.dateDayFormat(row?.start_at) ?? "-"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: columnWidths.formStatus[3] }}
                              pr="15px"
                            >
                              <Typography fontWeight="400" fontSize="16px">
                                {Util.dateDayFormat(row?.end_at) ?? "-"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: columnWidths.formStatus[4] }}
                              pr="15px"
                            >
                              <Typography
                                fontWeight="400"
                                fontSize={16}
                                lineHeight="23.92px"
                                sx={{
                                  width: "100%",
                                }}
                              >
                                {statusBox(row?.form_status) ?? "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Divider width="100%" />
                      </Box>
                    );
                  } else {
                    return (
                      <Box px="20px" key={`emp-${index}`}>
                        <Box
                          sx={{
                            width: "100%",
                            position: "relative",
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              height: "96px",
                              background: "#FFF",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                            px="25px"
                          >
                            {goalProgressHeaders.map((header) => {
                              return (
                                <>
                                  {header.column === "employee" &&
                                    <Box
                                      sx={{ width: columnWidths.progress[0] }}
                                      pr="15px"
                                    >
                                      <Grid container flexWrap="nowrap">
                                        <Grid item display="flex" alignItems="center">
                                          <Avatar src={row?.employee_photo} />
                                        </Grid>
                                        <Grid
                                          item
                                          pl={2}
                                          sx={{ width: "calc(100% - 12px)" }}
                                        >
                                          <Grid container>
                                            <Grid
                                              item
                                              display="flex"
                                              alignItems="center"
                                            >
                                              <Typography
                                                fontSize="16px"
                                                fontWeight="500"
                                                sx={{
                                                  WebkitBoxOrient: "vertical",
                                                  WebkitLineClamp: 2,
                                                  display: "-webkit-box",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                {row?.employee_name ?? "-"}
                                              </Typography>
                                            </Grid>
                                          </Grid>
                                          <Grid container>
                                            <Grid
                                              item
                                              display="flex"
                                              alignItems="center"
                                            >
                                              <Typography
                                                fontSize="16px"
                                                fontWeight="400"
                                                color="#AAAAAA"
                                                sx={{
                                                  WebkitBoxOrient: "vertical",
                                                  WebkitLineClamp: 2,
                                                  display: "-webkit-box",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                {row?.employee_position ?? "-"} (
                                                {row?.employee_organization ?? "-"})
                                              </Typography>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    </Box>}
                                  {header.column === "goal_name" &&
                                    <Box
                                      sx={{ width: columnWidths.progress[1] }}
                                      pr="15px"
                                    >
                                      <BootstrapTooltip
                                        title={
                                          row?.goal_name && row?.goal_name !== ""
                                            ? row.goal_name
                                            : "-"
                                        }
                                        placement="top"
                                      >
                                        <Typography
                                          fontWeight="400"
                                          fontSize="16px"
                                          sx={{
                                            textOverflow: "ellipsis",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                          }}
                                        >
                                          {row?.goal_name && row?.goal_name !== ""
                                            ? row.goal_name
                                            : "-"}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>}
                                  {(header.column === "form_custom" || header.column === "category") &&
                                    <Box
                                      sx={{
                                        width: columnWidths.progress[2],
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                      pr="15px"
                                    >
                                      <BootstrapTooltip
                                        title={
                                          header.column === "form_custom" ?
                                            row?.form_custom?.tooltip && row?.form_custom?.tooltip?.length > 0 ?
                                              row.form_custom.tooltip.map((tt) => {
                                                return (
                                                  <p>
                                                    {
                                                      `${tt.label}: ${tt.value}`
                                                    }
                                                  </p>
                                                )
                                              }) : ""
                                            : row?.goal_category?.name &&
                                              row?.goal_category?.name !== ""
                                              ? row?.goal_category?.name
                                              : "-"
                                        }
                                        placement="top"

                                      >
                                        {
                                          header.column === "form_custom" ?
                                            <Typography fontWeight="400" fontSize="16px">{row?.form_custom?.value}</Typography>
                                            :
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "40px",
                                                height: "40px",
                                                background: `${row?.goal_category?.color}`,
                                                borderRadius: "5px",
                                              }}
                                            >
                                              <Icon
                                                sx={{ color: "#FFF", fontSize: "20px" }}
                                              >
                                                {row?.goal_category?.icon}
                                              </Icon>
                                            </Box>
                                        }
                                      </BootstrapTooltip>
                                    </Box>
                                  }
                                  {header.column === "goal_start_at" &&
                                    <Box
                                      sx={{ width: columnWidths.progress[3] }}
                                      pr="15px"
                                    >
                                      <Typography fontWeight="400" fontSize="16px">
                                        {row?.goal_start_at && row?.goal_start_at !== ""
                                          ? Util.dateDayFormat(row?.goal_start_at)
                                          : "-"}
                                      </Typography>
                                    </Box>
                                  }
                                  {header.column === "goal_end_at" &&
                                    <Box
                                      sx={{ width: columnWidths.progress[4] }}
                                      pr="15px"
                                    >
                                      <Typography
                                        fontWeight="400"
                                        fontSize={16}
                                        lineHeight="23.92px"
                                        sx={{
                                          width: "100%",
                                        }}
                                      >
                                        {row?.goal_end_at && row?.goal_end_at !== ""
                                          ? Util.dateDayFormat(row?.goal_end_at)
                                          : "-"}
                                      </Typography>
                                    </Box>
                                  }
                                  {header.column === "actual_value" &&
                                    <Box
                                      sx={{ width: columnWidths.progress[5] }}
                                      pr="15px"
                                    >
                                      <BootstrapTooltip
                                        title={
                                          <>
                                            {`${t(
                                              "dashboardManager.label.actualValue"
                                            )}: ${row?.actual_value &&
                                              row?.actual_value !== ""
                                              ? row?.actual_value
                                              : "-"
                                              }`}
                                            <br />
                                            {`${t(
                                              "dashboardManager.label.targetValue"
                                            )}: ${row?.metric_value &&
                                              row?.metric_value !== ""
                                              ? `${getMetricConditionTextByLang(
                                                row?.metric_condition
                                              )} ${row?.metric_value}`
                                              : "-"
                                              }`}
                                          </>
                                        }
                                        placement="top"
                                      >
                                        <Box
                                          sx={{ cursor: "pointer" }}
                                          onClick={() => getGoalProgressLogsData(row)}
                                        >
                                          <Typography
                                            fontWeight="500"
                                            fontSize={16}
                                            sx={{
                                              width: "100%",
                                            }}
                                            color={
                                              row?.actual_value
                                                ? validateActualValueColor(row)
                                                  ? "#5DA196"
                                                  : "#CC6058"
                                                : "#828282"
                                            }
                                          >
                                            {row?.actual_value ?? "-"}
                                          </Typography>
                                          {(row?.data_as_of ?? false) && (
                                            <Typography
                                              fontWeight="400"
                                              fontSize={12}
                                              sx={{
                                                width: "100%",
                                              }}
                                              color="#C4C4C4"
                                            >
                                              ({t("dashboardManager.label.resultAsOf")}{" "}
                                              {Util.dateDayFormat(row.data_as_of) ??
                                                "-"}
                                              )
                                            </Typography>
                                          )}
                                        </Box>
                                      </BootstrapTooltip>
                                    </Box>
                                  }
                                </>
                              );
                            })}
                            <Box
                              sx={{ width: columnWidths.progress[6] }}
                              pr="15px"
                            >
                              <Typography
                                fontWeight="400"
                                fontSize={16}
                                lineHeight="23.92px"
                                sx={{
                                  width: "100%",
                                }}
                              >
                                {statusBoxProgress(row?.progress_name) ?? "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Divider width="100%" />
                      </Box>
                    );
                  }
                })
              ) : (
                <Box
                  sx={{
                    pt: "50px",
                    pb: "50px",
                    textAlign: "center",
                  }}
                >
                  <Nodata />
                  <Typography
                    color="#A0A0A0"
                    fontWeight="500"
                    fontSize={20}
                    lineHeight="30px"
                  >
                    {t("label.noData")}
                  </Typography>
                </Box>
              )}
              <Grid container mt={1} spacing={2} px="20px" pb="24px">
                <Grid item xs sm md display="flex" alignItems="center">
                  <Box mr={1}>
                    <Typography variant="s14_fw400">
                      {t("label.showMaxItems")}
                    </Typography>
                  </Box>
                  <Select
                    id="pagination"
                    variant="outlined"
                    value={perPage}
                    onChange={handlePerPageChange}
                    displayEmpty
                    size="small"
                    className="white-bg"
                  >
                    <MenuItem value={50}>
                      <Typography style={{ fontWeight: "300" }}>50</Typography>
                    </MenuItem>
                    <MenuItem value={100}>
                      <Typography style={{ fontWeight: "300" }}>100</Typography>
                    </MenuItem>
                    <MenuItem value={200}>
                      <Typography style={{ fontWeight: "300" }}>200</Typography>
                    </MenuItem>
                  </Select>
                </Grid>
                <Grid item display="flex" alignItems="center" ml="auto">
                  <Typography variant="s14_fw400">
                    {t("label.page")} {currentPage} {t("label.from")}{" "}
                    {totalPage}
                  </Typography>
                </Grid>
                <Grid item ml="auto" display="flex" alignItems="center">
                  <CustomPagination
                    setCurrentPage={handleSetCurrentPage}
                    selected={currentPage}
                    count={totalPage}
                  ></CustomPagination>
                </Grid>
              </Grid>
            </Paper>
            {/* history dialog */}
            <HistoryDialog
              open={openHistoryDialog}
              setValue={setHistoryDialog}
              data={historyData}
              optionalData={historyOptionalData}
            />
          </Box>
        </Grid>
      </Grid>
      {Util.ScrollToTop()}
    </>
  );
};
export default GoalManagerDashboard;
